// ./src/concurrency.ts
//
// ROADMAP #131 — a bulk action over a selection runs a FEW requests at a time, not all at once.
//
// ⚠ `Promise.all(ids.map(save))` fires every request in the same instant. Over HTTP/1.1 a browser sends six to a host
// and queues the rest — and since #122 every request carries a time limit that starts when it is CALLED, so the ones
// still queued behind a slow connection ran out of time without ever being sent (and the server, uncapped, took a burst
// of hundreds at once). With a few in flight, each request's clock starts when it actually goes out.

/** How many requests a bulk action keeps in flight — under the browser's six per host, so none waits unsent. */
export const BULK_CONCURRENCY = 4;

/**
 * `items.map(fn)` with at most `limit` calls in flight; the results come back in the items' order, as from
 * `Promise.all`. A call that rejects rejects the whole, as `Promise.all` does.
 */
export const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let next = 0;

  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await fn(items[index], index);
    }
  };

  const workers = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workers }, () => worker()));

  return results;
};
